import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type {
  GetLinkedinConnectProgressResponse,
  GetLinkedinConnectionResponse,
  StartLinkedinConnectResponse,
} from '@dreamai/types';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Modal } from 'react-bootstrap';
import { ReactComponent as LinkedinIcon } from '../../icons/linkedin-blue.svg';
import api from '../../api';
import CredentialSheet from '../chat/sidebar/CredentialSheet';
import SecureInputStage from '../chat/sidebar/SecureInputStage';
import '../chat/sidebar/secure-input-sheet.scss';
import '../chat/sidebar/payment-challenge-modal.scss';
import './daily-briefing-section.scss';
import CircleMinusIcon from './CircleMinusIcon';
import {
  phoneScreen,
  PhoneKeyboard,
  type TypeInput,
} from './OcadoConnectedApp';

const POLL_MS = 3000;
const INITIAL_CONNECTION: GetLinkedinConnectionResponse = {
  state: 'none',
  lastReadAt: null,
  autoArchive: true,
};

export function useLinkedinConnection(enabled: boolean): {
  connection: GetLinkedinConnectionResponse;
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const { getAccessTokenSilently } = useAuth0();
  const [connection, setConnection] =
    useState<GetLinkedinConnectionResponse>(INITIAL_CONNECTION);
  const [loading, setLoading] = useState(enabled);
  const refresh = useCallback(async () => {
    if (!enabled) {
      setConnection(INITIAL_CONNECTION);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.get<GetLinkedinConnectionResponse>(
        '/linkedin-connection',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setConnection(data);
    } catch (error) {
      console.error('Error loading LinkedIn connection:', error);
    } finally {
      setLoading(false);
    }
  }, [enabled, getAccessTokenSilently]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return { connection, loading, refresh };
}

type ConnectPhase =
  | 'opening'
  | 'live'
  | 'connected'
  | 'expired'
  | 'busy'
  | 'error';

const statusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const response = (error as { response?: { status?: unknown } }).response;
  return typeof response?.status === 'number' ? response.status : undefined;
};

const LinkedinConnectSheet: React.FC<{
  assistantName: string;
  onClose: (connected: boolean) => void;
}> = ({ assistantName, onClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [phase, setPhase] = useState<ConnectPhase>('opening');
  const [liveViewUrl, setLiveViewUrl] = useState<string | null>(null);
  const phaseRef = useRef<ConnectPhase>('opening');
  phaseRef.current = phase;
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [phone] = useState(phoneScreen);

  const auth = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post<StartLinkedinConnectResponse>(
          '/linkedin-connection/connect',
          {},
          await auth(),
        );
        if (cancelled) return;
        setLiveViewUrl(data.liveViewUrl);
        setPhase('live');
      } catch (error) {
        if (!cancelled) setPhase(statusOf(error) === 409 ? 'busy' : 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const typeIntoLinkedin = useCallback(
    async (input: TypeInput) => {
      await api.post('/linkedin-connection/connect/type', input, await auth());
    },
    [auth],
  );

  useEffect(() => {
    if (phase !== 'live') return undefined;
    let stopped = false;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const { data } = await api.get<GetLinkedinConnectProgressResponse>(
          '/linkedin-connection/connect',
          await auth(),
        );
        if (stopped || phaseRef.current !== 'live') return;
        if (data.state === 'connected') {
          setLiveViewUrl(null);
          setPhase('connected');
          return;
        }
        if (data.state === 'expired') {
          setLiveViewUrl(null);
          setPhase('expired');
          return;
        }
      } catch {
        // Keep polling; a blip shouldn't close the sign-in.
      }
      if (!stopped) timer = window.setTimeout(poll, POLL_MS);
    };
    timer = window.setTimeout(poll, POLL_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [phase, auth]);

  const close = useCallback(async () => {
    const connected = phaseRef.current === 'connected';
    if (!connected) {
      try {
        await api.post('/linkedin-connection/connect/cancel', {}, await auth());
      } catch {
        // The hosted sign-in session expires on its own.
      }
    }
    setLiveViewUrl(null);
    onClose(connected);
  }, [auth, onClose]);

  const footer =
    phase === 'live' ? (
      <button type='button' className='tx-btn-text' onClick={close}>
        Cancel
      </button>
    ) : phase === 'opening' ? undefined : (
      <button type='button' className='tx-btn-primary' onClick={close}>
        {phase === 'connected' ? 'Done' : 'Close'}
      </button>
    );

  return (
    <CredentialSheet
      show
      onClose={close}
      className={`secure-input-sheet payment-challenge-modal linkedin-connect-sheet${
        phase === 'live' ? ' live' : ''
      }`}
      ariaLabelledBy='linkedin-connect-title'
      holdOpen={phase === 'opening' || phase === 'live'}
      footer={footer}
    >
      {phase === 'opening' && (
        <SecureInputStage
          kind='signing-in'
          title='Opening LinkedIn…'
          titleId='linkedin-connect-title'
          busy
        />
      )}
      {phase === 'live' && (
        <>
          <div className='tx-head'>
            <h2 className='tx-title hero' id='linkedin-connect-title'>
              Log in <span className='accent'>to LinkedIn</span>
            </h2>
            <p className='brief'>
              {phone ? (
                <>
                  This is LinkedIn&rsquo;s own page. Tap a box on it, then type
                  below. {assistantName} passes what you type to LinkedIn and
                  never keeps it.
                </>
              ) : (
                <>
                  This is LinkedIn&rsquo;s own page. {assistantName} never sees
                  your password or code.
                </>
              )}
            </p>
          </div>
          {liveViewUrl && (
            <div className='live-view'>
              <span
                tabIndex={0}
                aria-hidden='true'
                className='tab-catch'
                data-testid='linkedin-tab-before'
                onFocus={() => frameRef.current?.focus()}
              />
              <iframe
                ref={frameRef}
                title='LinkedIn log in'
                src={liveViewUrl}
                sandbox='allow-same-origin allow-scripts allow-forms allow-modals'
                allow='clipboard-read; clipboard-write'
                className={phone ? 'phone-live-view' : undefined}
              />
              <span
                tabIndex={0}
                aria-hidden='true'
                className='tab-catch'
                data-testid='linkedin-tab-after'
                onFocus={() => frameRef.current?.focus()}
              />
            </div>
          )}
          {liveViewUrl && phone && (
            <PhoneKeyboard appName='LinkedIn' send={typeIntoLinkedin} />
          )}
        </>
      )}
      {phase === 'connected' && (
        <SecureInputStage
          kind='signed-in'
          title='LinkedIn connected'
          titleId='linkedin-connect-title'
        >
          <p>You can close this window.</p>
        </SecureInputStage>
      )}
      {phase === 'expired' && (
        <SecureInputStage
          kind='error'
          title='LinkedIn sign-in expired'
          titleId='linkedin-connect-title'
        >
          <p>Close this window and try again.</p>
        </SecureInputStage>
      )}
      {phase === 'busy' && (
        <SecureInputStage
          kind='error'
          title='LinkedIn is busy'
          titleId='linkedin-connect-title'
        >
          <p>Another sign-in is already open. Try again in a minute.</p>
        </SecureInputStage>
      )}
      {phase === 'error' && (
        <SecureInputStage
          kind='error'
          title="Couldn't open LinkedIn"
          titleId='linkedin-connect-title'
        >
          <p>Please try again in a minute.</p>
        </SecureInputStage>
      )}
    </CredentialSheet>
  );
};

const relativeTime = (timestamp: string) =>
  formatDistanceToNow(parseISO(timestamp), { addSuffix: true });

export const LinkedinConnectedApp: React.FC<{
  connection: GetLinkedinConnectionResponse;
  loading: boolean;
  assistantName: string;
  header: React.ReactNode;
  onChanged: () => Promise<void>;
}> = ({ connection, loading, assistantName, header, onChanged }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [autoArchive, setAutoArchive] = useState(connection.autoArchive);
  const [savingSetting, setSavingSetting] = useState(false);
  const [settingError, setSettingError] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState(false);
  const prepareAttemptedRef = useRef(false);
  const prepareSentRef = useRef(false);
  const connectingRef = useRef(connecting);
  const connectionStateRef = useRef(connection.state);
  const getAccessTokenRef = useRef(getAccessTokenSilently);
  connectingRef.current = connecting;
  connectionStateRef.current = connection.state;
  getAccessTokenRef.current = getAccessTokenSilently;

  useEffect(() => {
    if (
      loading ||
      connection.state === 'connected' ||
      prepareAttemptedRef.current
    ) {
      return undefined;
    }
    prepareAttemptedRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAccessTokenSilently();
        if (cancelled) return;
        prepareSentRef.current = true;
        await api.post(
          '/linkedin-connection/connect/prepare',
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch {
        // Preparation is opportunistic; Connect can start a session itself.
      }
    })();
    return () => {
      cancelled = true;
      if (!prepareSentRef.current) prepareAttemptedRef.current = false;
    };
  }, [loading, connection.state, getAccessTokenSilently]);

  useEffect(
    () => () => {
      if (
        !prepareSentRef.current ||
        connectingRef.current ||
        connectionStateRef.current === 'connected'
      ) {
        return;
      }
      void (async () => {
        try {
          const token = await getAccessTokenRef.current();
          await api.post(
            '/linkedin-connection/connect/cancel',
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch {
          // The hosted sign-in session expires on its own.
        }
      })();
    },
    [],
  );

  useEffect(() => {
    setAutoArchive(connection.autoArchive);
  }, [connection.autoArchive]);

  const handleConnectDone = useCallback(
    async (connected: boolean) => {
      connectingRef.current = false;
      setConnecting(false);
      if (connected) await onChanged();
    },
    [onChanged],
  );

  const openConnect = () => {
    connectingRef.current = true;
    setConnecting(true);
  };

  const handleToggle = async () => {
    if (savingSetting) return;
    const previous = autoArchive;
    const next = !previous;
    setAutoArchive(next);
    setSavingSetting(true);
    setSettingError(false);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.patch<GetLinkedinConnectionResponse>(
        '/linkedin-connection/settings',
        { autoArchive: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAutoArchive(data.autoArchive);
    } catch {
      setAutoArchive(previous);
      setSettingError(true);
    } finally {
      setSavingSetting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setDisconnectError(false);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/linkedin-connection/disconnect',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setConfirmDisconnect(false);
      await onChanged();
    } catch {
      setDisconnectError(true);
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      {header}
      <div className='ca-detail-card linkedin-connected-app'>
        <div className='ca-detail-block'>
          <h3 className='ca-block-title'>LinkedIn</h3>
          {loading ? (
            <div className='settings-skeleton' />
          ) : connection.state === 'none' ? (
            <button
              type='button'
              className='ca-connect-btn'
              onClick={openConnect}
            >
              <LinkedinIcon aria-hidden='true' />
              <span>Connect LinkedIn</span>
            </button>
          ) : (
            <div className='ca-account-list'>
              <div className='ca-account-row'>
                <div className='ca-account-info'>
                  <span className='ca-account-icon'>
                    <LinkedinIcon aria-hidden='true' />
                  </span>
                  <span className='ca-account-label'>LinkedIn</span>
                  {connection.state === 'logged_out' ? (
                    <span className='ca-account-limited'>Signed out</span>
                  ) : (
                    <span className='ca-app-status'>Connected</span>
                  )}
                  {connection.state === 'connected' &&
                    connection.lastReadAt && (
                      <span className='ca-account-scope'>
                        Last checked {relativeTime(connection.lastReadAt)}
                      </span>
                    )}
                </div>
                <button
                  type='button'
                  className='ca-remove-btn'
                  onClick={() => {
                    setDisconnectError(false);
                    setConfirmDisconnect(true);
                  }}
                  disabled={disconnecting}
                  aria-label='Disconnect LinkedIn'
                >
                  <CircleMinusIcon />
                </button>
              </div>
              {connection.state === 'logged_out' && (
                <button
                  type='button'
                  className='ca-connect-btn'
                  onClick={openConnect}
                >
                  <LinkedinIcon aria-hidden='true' />
                  <span>Reconnect LinkedIn</span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className='daily-briefing-section linkedin-auto-archive'>
          <div className='daily-briefing-section-row'>
            <div className='daily-briefing-section-row-info'>
              <span className='daily-briefing-section-row-label'>
                Archive conversations that don&apos;t need you
              </span>
            </div>
            <button
              type='button'
              className={`toggle-switch${autoArchive ? ' on' : ''}`}
              onClick={handleToggle}
              disabled={savingSetting}
              aria-label="Archive conversations that don't need you"
              role='switch'
              aria-checked={autoArchive}
            >
              <span className='toggle-knob' />
            </button>
          </div>
          {settingError && (
            <p className='ca-block-description' role='alert'>
              Couldn&apos;t update this setting. Please try again.
            </p>
          )}
        </div>
      </div>

      {connecting && (
        <LinkedinConnectSheet
          assistantName={assistantName}
          onClose={handleConnectDone}
        />
      )}

      <Modal
        show={confirmDisconnect}
        onHide={() => !disconnecting && setConfirmDisconnect(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Disconnect LinkedIn?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {assistantName} will stop checking your LinkedIn inbox.
          {disconnectError && (
            <p className='ca-block-description' role='alert'>
              Couldn&apos;t disconnect LinkedIn. Try again.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type='button'
            className='tx-btn-text'
            disabled={disconnecting}
            onClick={() => setConfirmDisconnect(false)}
          >
            Cancel
          </button>
          <button
            type='button'
            className='tx-btn-primary'
            disabled={disconnecting}
            onClick={handleDisconnect}
          >
            Disconnect
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
